import { reviewHoldKind, reviewReadinessIssues, type ReviewFinding, type ReviewPackage } from "./review-domain.ts";

export type ReviewQueueFilter = "attention" | "all" | "approved" | "holds" | "excluded";

export type ReviewQueueSummary = {
  total: number;
  attention: number;
  approved: number;
  holds: number;
  excluded: number;
};

export type ReviewAssetGroup = {
  assetIndex: number;
  assetId: string;
  assetName: string;
  reportNumber: string;
  manufacturer: string;
  modelNumber: string;
  serialNumber: string;
  matchStatus: string;
  findings: ReviewFinding[];
};

export function groupReviewFindingsByAsset(findings: ReviewFinding[]): ReviewAssetGroup[] {
  const groups = new Map<number, ReviewAssetGroup>();
  for (const finding of findings) {
    const existing = groups.get(finding.assetIndex);
    if (existing) {
      existing.findings.push(finding);
      continue;
    }
    groups.set(finding.assetIndex, {
      assetIndex: finding.assetIndex,
      assetId: finding.assetId,
      assetName: finding.assetName,
      reportNumber: finding.reportNumber,
      manufacturer: finding.manufacturer,
      modelNumber: finding.modelNumber,
      serialNumber: finding.serialNumber,
      matchStatus: finding.matchStatus,
      findings: [finding],
    });
  }
  return [...groups.values()];
}

function needsAttention(finding: ReviewFinding, issueIds: Set<string>) {
  return issueIds.has(finding.id)
    || finding.decision === "clarification"
    || reviewHoldKind(finding) === "identity";
}

export function summarizeReviewQueue(review: ReviewPackage): ReviewQueueSummary {
  const issueIds = new Set(reviewReadinessIssues(review).map((issue) => issue.findingId));
  return {
    total: review.findings.length,
    attention: review.findings.filter((finding) => needsAttention(finding, issueIds)).length,
    approved: review.findings.filter((finding) => finding.decision === "estimate" && !issueIds.has(finding.id)).length,
    holds: review.findings.filter((finding) => finding.decision === "hold").length,
    excluded: review.findings.filter((finding) => finding.decision === "excluded").length,
  };
}

export function filterReviewQueue(review: ReviewPackage, filter: ReviewQueueFilter) {
  if (filter === "all") return [...review.findings];
  const issueIds = new Set(reviewReadinessIssues(review).map((issue) => issue.findingId));
  if (filter === "attention") return review.findings.filter((finding) => needsAttention(finding, issueIds));
  if (filter === "approved") return review.findings.filter((finding) => finding.decision === "estimate" && !issueIds.has(finding.id));
  if (filter === "holds") return review.findings.filter((finding) => finding.decision === "hold");
  return review.findings.filter((finding) => finding.decision === "excluded");
}
